import {
  type ComponentProps,
  type FunctionComponent,
  type MouseEvent,
  type PropsWithChildren,
  useEffect,
} from 'react';
import clsx from 'clsx';
import { useTransitionState } from 'react-transition-state';
import { Portal } from './Portal';

export interface BackdropProps extends ComponentProps<'div'> {
  /**
   * On close
   * @param event
   */
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Is visible
   */
  isVisible?: boolean;
  /**
   * Sweep Mode™
   * (makes it appear from the sides instead of from everywhere)
   */
  sweep?: boolean;
  /**
   * Target ID for the Portal component
   */
  target?: ComponentProps<typeof Portal>['target'];
}

export const Backdrop: FunctionComponent<PropsWithChildren<BackdropProps>> = ({
  children,
  className = 'flex justify-center',
  onClose,
  isVisible,
  target = 'backdrop',
  sweep = false,
  ...attrs
}) => {
  const [state, toggle] = useTransitionState({
    timeout: { enter: 0, exit: 500 },
    mountOnEnter: true,
    unmountOnExit: true,
    preEnter: true,
  });

  const { status, isMounted } = state;

  useEffect(() => {
    toggle(isVisible);
  }, [isVisible, toggle]);

  const animated = status === 'preEnter' || status === 'exiting';

  return (
    <Portal target={target}>
      {isMounted && (
        <div
          {...attrs}
          role="presentation"
          className={clsx(className, 'fixed inset-0 isolate items-center')}
        >
          <button
            aria-hidden="true"
            className={clsx('absolute inset-0 -z-1 transition-all duration-500', {
              'bg-white/80 backdrop-blur': !animated,
              'bg-white/0 backdrop-blur-none': animated,
              '-translate-x-full': sweep && (status === 'preEnter' || status === 'entering'),
              'translate-x-full': sweep && status === 'exiting',
            })}
            onClick={onClose}
            type="button"
          />
          {children}
        </div>
      )}
    </Portal>
  );
};
